import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import { useValidateInvite, useRedeemInvite } from '@/hooks/useInviteRequest';

export function InviteRedemption() {
  const { token } = useParams<{ token: string }>();
  const { data: invite, isLoading, error } = useValidateInvite(token ?? '');
  const redeemInvite = useRedeemInvite();
  const [redeemed, setRedeemed] = useState(false);
  const [redeemMessage, setRedeemMessage] = useState('');

  const handleRedeem = async () => {
    if (!token) return;

    try {
      const result = await redeemInvite.mutateAsync({ token });
      setRedeemMessage(result.message);
      setRedeemed(true);
    } catch (err) {
      setRedeemMessage(
        err instanceof Error ? err.message : 'Failed to redeem invite. Please try again.'
      );
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-gray-900 via-gray-900 to-gray-950">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-indigo-500 border-t-transparent" />
          <p className="mt-4 text-gray-400">Validating invite...</p>
        </div>
      </div>
    );
  }

  if (error || !invite) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-gray-900 via-gray-900 to-gray-950">
        <div className="mx-auto max-w-md rounded-lg border border-gray-700 bg-gray-800 p-8 text-center">
          <div className="mb-4 flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-500/20">
              <svg
                className="h-8 w-8 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                role="img"
                aria-label="Invalid"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-white">Invalid or Expired Invite</h1>
          <p className="mt-2 text-gray-400">
            This invite link is invalid, has expired, or has already been used.
          </p>
          <Link
            to="/"
            className="mt-6 inline-block text-indigo-400 hover:text-indigo-300"
          >
            Return to homepage
          </Link>
        </div>
      </div>
    );
  }

  if (redeemed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-gray-900 via-gray-900 to-gray-950">
        <div className="mx-auto max-w-md rounded-lg border border-gray-700 bg-gray-800 p-8 text-center">
          <div className="mb-4 flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-500/20">
              <svg
                className="h-8 w-8 text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                role="img"
                aria-label="Success"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-white">Welcome!</h1>
          <p className="mt-2 text-gray-400">{redeemMessage}</p>
          <Link
            to="/"
            className="mt-6 inline-block rounded-md bg-gradient-to-r from-indigo-500 to-purple-500 px-6 py-3 font-medium text-white hover:from-indigo-600 hover:to-purple-600"
          >
            Explore Podcasts
          </Link>
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-gray-900 via-gray-900 to-gray-950">
      <div className="mx-auto max-w-md rounded-lg border border-gray-700 bg-gray-800 p-8">
        <div className="mb-6 text-center">
          <div className="mb-4 flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-indigo-500/20">
              <svg
                className="h-8 w-8 text-indigo-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                role="img"
                aria-label="Email"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-white">
            You're Invited!
          </h1>
          <p className="mt-2 text-gray-400">
            Welcome to Kaster Media! You're ready to start creating AI-powered podcasts.
          </p>
        </div>

        <div className="mb-6 rounded-md bg-gray-700/50 p-4">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">Invited:</span>
              <span className="text-white">{formatDate(invite.sentAt)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Expires:</span>
              <span className="text-white">{formatDate(invite.expiresAt)}</span>
            </div>
          </div>
        </div>

        {redeemInvite.isError && (
          <div className="mb-4 rounded-md bg-red-900/50 p-3 text-sm text-red-300">
            {redeemMessage || 'Failed to redeem invite. Please try again.'}
          </div>
        )}

        <button
          type="button"
          onClick={handleRedeem}
          disabled={redeemInvite.isPending}
          className="w-full rounded-md bg-gradient-to-r from-indigo-500 to-purple-500 px-4 py-3 font-medium text-white hover:from-indigo-600 hover:to-purple-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:ring-offset-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {redeemInvite.isPending ? 'Accepting...' : 'Accept Invitation'}
        </button>

        <p className="mt-4 text-center text-xs text-gray-500">
          By accepting, you'll get access to create your own AI-powered podcasts.
        </p>
      </div>
    </div>
  );
}
